import type {UserImageAttachment, UserImageAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {createPendingImageAttachment} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import {clearPendingUpload, getPendingUploadFile, registerPendingUpload, updatePendingUploadPromise} from '@Components/ai-prompt-box/libraries/ai-prompt-box-pending-uploads.library';
import {getMaxAllowedUploadFileSize, getReadableMaxAllowedUploadFileSize} from '@Libraries/user-media-library';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {noop} from '@Utils/general.util';

export interface FileUploadPipelineState {
  promptBoxId: string;
  getAttachmentCount: () => number;
  setActive: () => void;
  addPendingImageAttachments: (attachments: UserImageAttachment[]) => void;
  resolvePendingAttachment: (localId: string, resolved: UserImageAttachment) => void;
  markPendingAttachmentFailed: (localId: string) => void;
  markPendingAttachmentUploading: (localId: string) => void;
}

export interface FileUploadPipelineConfig<TResponse> {
  maxAttachments: number;
  attachmentType: UserImageAttachmentType;
  uploadFile: (file: File) => Promise<TResponse>;
  resolveAttachment: (response: TResponse) => UserImageAttachment;
  acceptedFileTypes?: string[];
  onUnsupportedFiles?: VoidFunction;
  onAttachmentResolved?: (resolved: UserImageAttachment) => void;
}

interface UploadableFileItem<TResponse> {
  file: File;
  localId: string;
  resolveFn: (response: TResponse) => void;
  rejectFn: () => void;
}

interface PendingFileItem<TResponse> extends UploadableFileItem<TResponse> {
  attachment: UserImageAttachment;
  blobUrl: string;
  promise: Promise<TResponse>;
}

interface DeferredUpload<TResponse> {
  promise: Promise<TResponse>;
  resolveFn: (response: TResponse) => void;
  rejectFn: () => void;
}

interface FilteredFiles {
  accepted: File[];
  unsupported: File[];
  oversized: File[];
}

const ERROR_GROWL_INTERVAL_MS = 7000;

export const handleFilesUpload = <TResponse>(files: File[], state: FileUploadPipelineState, config: FileUploadPipelineConfig<TResponse>): void => {
  const currentCount = state.getAttachmentCount();
  if (currentCount >= config.maxAttachments) {
    return;
  }

  const filtered = filterFiles(files, config.acceptedFileTypes);
  if (filtered.unsupported.length > 0) {
    config.onUnsupportedFiles?.();
  }
  if (filtered.oversized.length > 0) {
    showOversizedFileGrowl(filtered.oversized.length);
  }
  if (filtered.accepted.length === 0) {
    return;
  }

  const filesToUpload = filtered.accepted.slice(0, config.maxAttachments - currentCount);
  const pendingItems = filesToUpload.map((file) => buildPendingItem<TResponse>(file, config.attachmentType));

  state.addPendingImageAttachments(pendingItems.map((item) => item.attachment));
  pendingItems.forEach((item) => {
    registerPendingUpload(state.promptBoxId, item.localId, item.blobUrl, item.promise, item.file);
  });
  state.setActive();

  void runUploadBatch(pendingItems, state, config);
};

export const retryFileUpload = <TResponse>(localId: string, state: FileUploadPipelineState, config: FileUploadPipelineConfig<TResponse>): void => {
  const file = getPendingUploadFile(state.promptBoxId, localId);
  if (!file) {
    return;
  }

  const {promise, resolveFn, rejectFn} = createDeferredUpload<TResponse>();
  updatePendingUploadPromise(state.promptBoxId, localId, promise);
  state.markPendingAttachmentUploading(localId);

  void uploadSingleFile({file, localId, resolveFn, rejectFn}, state, config);
};

/**
 * The pending promise stored in the registry is only awaited later, when the user submits.
 * Until then we attach a no-op rejection handler so a failed upload doesn't surface as an
 * "Uncaught (in promise)" warning while the user is still deciding what to do.
 */
const createDeferredUpload = <TResponse>(): DeferredUpload<TResponse> => {
  let resolveFn!: (response: TResponse) => void;
  let rejectFn!: () => void;
  const promise = new Promise<TResponse>((resolve, reject) => {
    resolveFn = resolve;
    rejectFn = reject;
  });
  promise.catch(noop);
  return {promise, resolveFn, rejectFn};
};

const buildPendingItem = <TResponse>(file: File, attachmentType: UserImageAttachmentType): PendingFileItem<TResponse> => {
  const {attachment, localId, blobUrl} = createPendingImageAttachment(file, attachmentType);
  const {promise, resolveFn, rejectFn} = createDeferredUpload<TResponse>();
  return {file, attachment, localId, blobUrl, resolveFn, rejectFn, promise};
};

const runUploadBatch = async <TResponse>(items: PendingFileItem<TResponse>[], state: FileUploadPipelineState, config: FileUploadPipelineConfig<TResponse>): Promise<void> => {
  await Promise.allSettled(items.map((item) => uploadSingleFile(item, state, config)));
};

const uploadSingleFile = async <TResponse>(item: UploadableFileItem<TResponse>, state: FileUploadPipelineState, config: FileUploadPipelineConfig<TResponse>): Promise<void> => {
  try {
    const response = await config.uploadFile(item.file);
    onUploadSuccess(response, item, state, config);
  } catch {
    onUploadError(item, state);
  }
};

const onUploadSuccess = <TResponse>(response: TResponse, item: UploadableFileItem<TResponse>, state: FileUploadPipelineState, config: FileUploadPipelineConfig<TResponse>): void => {
  const resolved = config.resolveAttachment(response);
  state.resolvePendingAttachment(item.localId, resolved);
  clearPendingUpload(state.promptBoxId, item.localId);
  item.resolveFn(response);
  config.onAttachmentResolved?.(resolved);
};

const onUploadError = <TResponse>(item: UploadableFileItem<TResponse>, state: FileUploadPipelineState): void => {
  state.markPendingAttachmentFailed(item.localId);
  item.rejectFn();
};

const filterFiles = (files: File[], acceptedFileTypes?: string[]): FilteredFiles => {
  const maxSize = getMaxAllowedUploadFileSize();
  const result: FilteredFiles = {accepted: [], unsupported: [], oversized: []};

  files.forEach((file) => {
    if (acceptedFileTypes && !acceptedFileTypes.includes(file.type)) {
      result.unsupported.push(file);
      return;
    }
    if (file.size > maxSize) {
      result.oversized.push(file);
      return;
    }
    result.accepted.push(file);
  });

  return result;
};

const showOversizedFileGrowl = (count: number): void => {
  const text =
    count === 1
      ? window.i18next.t('pmwjs_upload_some_media_file_exceeding_limit', {uploadFileSizeLimit: getReadableMaxAllowedUploadFileSize()})
      : window.i18next.t('pmwjs_upload_some_media_files_exceeding_limit', {numfiles: count, uploadFileSizeLimit: getReadableMaxAllowedUploadFileSize()});

  showMessageGrowl({
    type: GROWL_TYPE.DANGER,
    text,
    showCloseIcon: true,
    interval: ERROR_GROWL_INTERVAL_MS,
  });
};
